import { useCallback, useEffect, useState } from 'react';
import {
    type ApolloError,
    type Currency,
    getValidationErrorMessages,
    useUpdateExtraCreditPurchaseMutation,
    useUpdatePackagePurchaseMutation,
} from '@lingoda/monolith-graphql';
import { gqlErrorsToToasts, showToast, showToasts } from '@lingoda/toasts';
import type { CheckoutPurchase } from '../types';

export const useCurrencyChange = (purchase: CheckoutPurchase) => {
    const currencyFromPurchase = purchase.total.currency;
    const [currency, setCurrency] = useState<Currency | undefined>(currencyFromPurchase);

    useEffect(() => {
        setCurrency(currencyFromPurchase);
    }, [currencyFromPurchase]);

    const handleError = useCallback(
        (error: ApolloError) => {
            setCurrency(currencyFromPurchase);

            const toasts = gqlErrorsToToasts(getValidationErrorMessages(error));
            if (toasts.length) {
                showToasts(toasts);
            } else {
                showToast('error', error.message);
            }
        },
        [currencyFromPurchase],
    );

    const [updatePackagePurchase] = useUpdatePackagePurchaseMutation({ onError: handleError });
    const [updateCreditPurchase] = useUpdateExtraCreditPurchaseMutation({ onError: handleError });

    const purchaseId = purchase.id;
    const isExtraCreditPurchase = purchase.__typename === 'ExtraCreditPurchase';

    const handleCurrencyChange = useCallback(
        (newCurrency: Currency) => {
            setCurrency(newCurrency);

            const updateRequest = {
                id: purchaseId,
                currency: newCurrency,
            };

            if (isExtraCreditPurchase) {
                void updateCreditPurchase({ variables: updateRequest });
            } else {
                void updatePackagePurchase({ variables: updateRequest });
            }
        },
        [purchaseId, isExtraCreditPurchase, updateCreditPurchase, updatePackagePurchase],
    );

    return [currency, handleCurrencyChange] as const;
};
